import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, message, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { SUBMIT_BATCH_ADJUSTMENT } from '../graphql/mutations/submitBatchAdjustment';
import { GET_ALL_BATCH_ADJUSTMENT_TASKS, BatchAdjustmentTaskResponse, BatchAdjustmentTaskType } from '../graphql/queries/getAllBatchAdjustmentTasks';
import {ResourceCode} from "../../../../../constants/resourceCodes";
import {HasResource} from "../../../../../components/HasResource";

interface AdjustmentModalProps {
    visible: boolean;
    onCancel: () => void;
}

const AdjustmentModal: React.FC<AdjustmentModalProps> = ({ visible, onCancel }) => {
    const [uploading, setUploading] = useState(false);

    // GraphQL查询调账任务列表
    const { data, loading, refetch } = useQuery<BatchAdjustmentTaskResponse>(GET_ALL_BATCH_ADJUSTMENT_TASKS);

    // GraphQL提交调账Mutation
    const [submitBatchAdjustment] = useMutation(SUBMIT_BATCH_ADJUSTMENT);

    // 当弹窗打开时刷新数据
    useEffect(() => {
        if (visible) {
            refetch();
        }
    }, [visible, refetch]);

    // 下载模板
    const handleDownloadTemplate = () => {
        // 创建CSV内容，包含3列：客户ID，调账类型，调账金额
        const csvHeader = '客户ID,调账类型(手动调账:6/赔付交易:7),调账金额(调增输入正数/调减输入负数，赔付仅支持正数，单笔不超过9999999)\n';
        const csvContent = csvHeader;

        // 创建Blob对象
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });

        // 创建下载链接
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', '调账模板.csv');
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 释放URL对象
        URL.revokeObjectURL(url);

        message.success('调账模板下载成功');
    };

    // 上传调账数据
    const handleUploadAdjustment = async (file: UploadFile) => {
        if (!file) return false;

        // 检查文件类型
        const isCSV = file.type === 'text/csv' || file.name?.endsWith('.csv');
        if (!isCSV) {
            message.error('只能上传CSV格式文件！');
            return false;
        }

        setUploading(true);

        try {
            // 读取文件内容
            const fileContent = await readFileAsArrayBuffer(file as any);
            const base64Content = arrayBufferToBase64(fileContent);

            // 调用GraphQL Mutation
            await submitBatchAdjustment({
                variables: {
                    csvContentBase64: base64Content,
                    submitter: 'Current User',
                },
            });

            message.success('调账数据上传成功，正在处理中...');

            // 刷新列表
            setTimeout(() => {
                refetch();
            }, 1000);
        } catch (error: any) {
            console.error('上传失败:', error);
            message.error(`上传失败: ${error.message || '未知错误'}`);
        } finally {
            setUploading(false);
        }

        return false; // 阻止默认上传行为
    };

    // 读取文件为ArrayBuffer
    const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target?.result) {
                    resolve(e.target.result as ArrayBuffer);
                } else {
                    reject(new Error('读取文件失败'));
                }
            };
            reader.onerror = () => reject(new Error('读取文件失败'));
            reader.readAsArrayBuffer(file);
        });
    };

    // 将ArrayBuffer转为Base64
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    };

    // 下载调账明细
    const handleDownloadDetail = (record: BatchAdjustmentTaskType) => {
        if (!record.resultFileUrl) {
            message.warning('结果文件尚未生成');
            return;
        }

        // 直接在新窗口打开下载链接
        window.open(record.resultFileUrl, '_blank');
        message.success('开始下载调账明细');
    };

    const columns: ColumnsType<BatchAdjustmentTaskType> = [
        {
            title: '调账任务ID',
            dataIndex: 'id',
            key: 'id',
            width: 180,
        },
        {
            title: '调账任务提交人',
            dataIndex: 'submitterName',
            key: 'submitterName',
            width: 120,
        },
        {
            title: '调账明细数量',
            dataIndex: 'detailCount',
            key: 'detailCount',
            width: 100,
            align: 'center',
        },
        {
            title: '成功数',
            dataIndex: 'successCount',
            key: 'successCount',
            width: 80,
            align: 'center',
            render: (count: number) => (
                <span className="text-green-600">{count}</span>
            ),
        },
        {
            title: '失败数',
            dataIndex: 'failCount',
            key: 'failCount',
            width: 80,
            align: 'center',
            render: (count: number) => (
                <span className={count > 0 ? 'text-red-600' : ''}>{count}</span>
            ),
        },
        {
            title: '任务状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
        },
        {
            title: '下载明细',
            key: 'download',
            width: 120,
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadDetail(record)}
                    disabled={!record.resultFileUrl}
                >
                    下载
                </Button>
            ),
        },
    ];

    return (
        <Modal
            title="调账操作"
            open={visible}
            onCancel={onCancel}
            width={900}
            footer={null}
        >
            <div className="flex flex-col" style={{ height: '600px' }}>
                {/* 顶部操作按钮 */}
                <div className="mb-4 space-x-4 flex-shrink-0">
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadTemplate}
                    >
                        下载模板
                    </Button>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_TRANS_QUERY]}>
                        <Upload
                            beforeUpload={handleUploadAdjustment}
                            showUploadList={false}
                            accept=".csv"
                            disabled={uploading}
                        >
                            <Button icon={<UploadOutlined />} loading={uploading}>
                                上传调账数据
                            </Button>
                        </Upload>
                    </HasResource>
                </div>

                {/* 历史调账记录表格 */}
                <div className="flex-1 overflow-hidden min-h-0">
                    <Table
                        columns={columns}
                        dataSource={data?.getAllBatchAdjustmentTasks || []}
                        rowKey="taskId"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 条记录`,
                            style: { marginTop: '16px' },
                        }}
                        scroll={{ y: 380 }}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default AdjustmentModal;
